import { useState } from "react";
import type { Image } from "@photography-booking/shared";
import { Section } from "../components/Section.js";
import { Lightbox } from "../components/Lightbox.js";
import { assetUrl } from "../api/client.js";
import { trackImageOpen } from "../lib/use-site-tracker.js";

interface GalleryProps {
  /**
   * Published images to render. Defaults to an empty list; a later task wires
   * the gallery data fetch. The empty-state placeholder satisfies Req 2.3.
   */
  images?: readonly Image[];
}

/**
 * Gallery section (Req 1.2, 2.2-2.4). Renders exactly one card per published
 * image, or only a placeholder message (and zero cards) when the portfolio is
 * empty. Selecting a card opens an enlarged lightbox view of that image with a
 * close affordance. Driven entirely by the `images` prop so the later
 * data-fetch wiring stays clean.
 */
/** How many images to show initially and reveal per "View more" click. */
const PAGE_SIZE = 8;

export function Gallery({ images = [] }: GalleryProps): JSX.Element {
  const [selectedImage, setSelectedImage] = useState<Image | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Only render the first `visibleCount` images so visitors see a compact set
  // (top 6) and reveal more on demand — avoids a long scroll on mobile.
  const visible = images.slice(0, visibleCount);
  const remaining = images.length - visible.length;

  return (
    <Section id="gallery" title="Gallery" subtitle="Selected work">
      {images.length === 0 ? (
        <p className="gallery__placeholder" role="status">
          New work is being curated. Please check back soon.
        </p>
      ) : (
        <>
          <ul className="gallery__grid">
            {visible.map((image) => (
              <li key={image.imageId} className="gallery__card">
                <div className="gallery__media">
                  <button
                    type="button"
                    className="gallery__card-button"
                    aria-label="View enlarged image"
                    onClick={() => {
                      // Measurement only; it cannot throw and the enlarged view
                      // opens regardless (Req 2.3, 14.5).
                      trackImageOpen(image.imageId);
                      setSelectedImage(image);
                    }}
                  >
                    <img
                      className="gallery__image"
                      src={assetUrl(image.renditionKey ?? image.originalKey)}
                      alt=""
                      loading="lazy"
                    />
                  </button>
                </div>
                {image.caption || image.photoCredit ? (
                  <div className="gallery__caption">
                    {image.caption ? (
                      <p className="gallery__comment">{image.caption}</p>
                    ) : null}
                    {image.photoCredit ? (
                      <p className="gallery__credit">
                        Photo by: {image.photoCredit}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
          {remaining > 0 ? (
            <div className="gallery__more-wrap">
              <button
                type="button"
                className="gallery__more"
                onClick={() =>
                  setVisibleCount((count) => count + PAGE_SIZE)
                }
              >
                View more ({remaining} more)
              </button>
            </div>
          ) : null}
        </>
      )}
      {selectedImage ? (
        <Lightbox
          image={selectedImage}
          onClose={() => setSelectedImage(null)}
        />
      ) : null}
    </Section>
  );
}
